import { useQuery } from '@tanstack/react-query';
import { API } from '@/utils/API';
import { SubmissionFormData } from '@/features/submission_forms/types';

export type SubmissionListScope = 'study' | 'site';

interface UseSubmissionsParams {
  ownerId?: string;
  studyId?: string;
  /** Owner-tab lists on study/site pages: STI rows plus base Submission for that Study or StudySite. */
  submissionScope?: SubmissionListScope;
  search?: string;
  status?: string;
  type?: string;
  order?: string;
  sort?: string;
  quickFilter?: string;
  /**
   * Opt-in server pagination — omit both to get the pre-existing
   * "return everything" contract (e.g. ScopedSubmissionsTable's owner-tab
   * lists, which are small and want every row rendered at once).
   */
  page?: number;
  perPage?: number;
}

export interface SubmissionsPagination {
  total: number;
  page?: number;
  per_page?: number;
  total_pages?: number;
}

interface SubmissionsResponse {
  resources: SubmissionFormData[];
  pagination: SubmissionsPagination;
}

const fetchSubmissions = async (
  params: UseSubmissionsParams
): Promise<SubmissionsResponse> => {
  const searchParams = new URLSearchParams({
    order: params.order || 'desc',
    sort: params.sort || 'created_at',
    search: params.search || '',
    status: params.status || '',
    type: params.type || '',
    ...(params.ownerId && { owner_id: params.ownerId }),
    ...(params.studyId && { study_id: params.studyId }),
    ...(params.submissionScope && { submission_scope: params.submissionScope }),
    ...(params.quickFilter && { quick_filter: params.quickFilter }),
    ...(params.page && {
      page: String(params.page),
      per_page: String(params.perPage || 25),
    }),
  });

  const response = await API.authenticated.fetch(
    `/submissions?${searchParams.toString()}`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch submissions');
  }

  return response.json();
};

export const useSubmissions = (params: UseSubmissionsParams) => {
  const query = useQuery({
    queryKey: ['submissions', params],
    queryFn: () => fetchSubmissions(params),
    staleTime: 2 * 60 * 1000, // Consider fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  return {
    ...query,
    data: query.data?.resources,
    pagination: query.data?.pagination,
  };
};
